import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { keys } from '../../api/keys'
import { AlertDialog, ConfirmDialog } from '../../platform/primitives/Dialog'
import { useToast } from '../../platform/feedback/ToastProvider'
import type { InstitutionGroup } from './connectionGroups'
import { relinkIdOf } from './connectionGroups'
import { relinkOutcomeOf, type RelinkOutcome, type RelinkOutcomeInput } from './relinkOutcome'
import { providerCode } from './providers/registry'
import type { ProviderType } from './providers/types'
import { useReauthFlow, useRelinkFlow, type ProviderItem } from './useAggregator'

type OutcomeContext = {
  /** The DISCONNECTED bank being reconnected; `null` where unknown (the OAuth return). */
  bank: string | null
  /** The provider the disconnected bank was linked with — what a `use_reauth` repair opens. */
  provider: ProviderType | null
  /** Navigate with `replace` (the OAuth return must not leave `/plaid` in history). */
  replace?: boolean
  /** Runs when the "different bank" dialog is dismissed. */
  onDifferentBankClose?: () => void
}

type Dialog =
  | { kind: 'different_bank'; title: string; body: string; onClose?: () => void }
  | { kind: 'use_reauth'; title: string; body: string; itemId: string; provider: ProviderType | null }

/**
 * The repair (`useReauthFlow`) result copy — the ONE table, shared by
 * `Connections` and `useApplyRelinkOutcome`: success, the manual-fallback
 * message, and the "still needs reconnecting" warning. `onSettled` runs after
 * each of the three (a caller clearing its own per-card busy flag).
 */
export function useReauthResultToasts(
  reauth: ReturnType<typeof useReauthFlow>,
  onSettled?: () => void,
): void {
  const toast = useToast()
  useEffect(() => {
    const { state } = reauth
    if (state.status === 'success') {
      toast.show({ kind: 'success', message: 'Reconnected.' })
    } else if (state.status === 'manual_fallback') {
      toast.show({ kind: 'error', message: state.message })
    } else if (state.status === 'not_repaired') {
      // `warning`, not `error`: the card's own health note already says so.
      toast.show({ kind: 'warning', message: 'This connection still needs reconnecting — try Reconnect again.' })
    } else {
      return
    }
    reauth.reset()
    onSettled?.()
  }, [reauth, toast, onSettled])
}

/**
 * Carries out `relinkOutcomeOf`'s answer: a toast, a navigation, or one of two
 * dialogs. **The one executor** for every entrance to a reconnect — the
 * `Connections` card, the Institutions panel, the Link prompt and the Plaid
 * OAuth return — so no screen keeps its own table of copy.
 *
 * Returns the outcome it applied so a caller that has nowhere to stay (the
 * OAuth return) can navigate on a `toast` or `silent`. `dialogs` is mounted by
 * the caller, beside whatever it renders.
 */
export function useApplyRelinkOutcome(): {
  apply: (input: RelinkOutcomeInput, ctx: OutcomeContext) => RelinkOutcome
  dialogs: ReactNode
} {
  const toast = useToast()
  const navigate = useNavigate()
  const client = useQueryClient()
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [repairing, setRepairing] = useState(false)
  const reauth = useReauthFlow()

  useReauthResultToasts(reauth)

  const apply = useCallback(
    (input: RelinkOutcomeInput, ctx: OutcomeContext): RelinkOutcome => {
      const outcome = relinkOutcomeOf(input, ctx.bank)
      switch (outcome.kind) {
        case 'toast':
          toast.show({ kind: outcome.tone, message: outcome.message })
          if (outcome.refreshAccounts) void client.invalidateQueries({ queryKey: keys.accounts.all() })
          break
        case 'navigate':
          navigate(outcome.to, { replace: ctx.replace === true })
          break
        case 'different_bank':
          setDialog({ kind: 'different_bank', title: outcome.title, body: outcome.body, onClose: ctx.onDifferentBankClose })
          break
        case 'use_reauth':
          setDialog({
            kind: 'use_reauth',
            title: outcome.title,
            body: outcome.body,
            itemId: outcome.itemId,
            provider: ctx.provider,
          })
          break
        case 'silent':
          break
        default:
          outcome satisfies never
      }
      return outcome
    },
    [toast, navigate, client],
  )

  const closeDialog = () => {
    const current = dialog
    setDialog(null)
    if (current?.kind === 'different_bank') current.onClose?.()
  }

  // The repair starts from a FRESH click (the dialog's own confirm), after the
  // dialog has closed, so no popup is blocked and the dialog never sits over
  // the Link UI.
  const repairTarget = useRef<{ itemId: string; provider: ProviderType } | null>(null)
  useEffect(() => {
    if (!repairing || dialog !== null) return
    setRepairing(false)
    const target = repairTarget.current
    repairTarget.current = null
    if (target !== null) void reauth.start(target.itemId, target.provider)
  }, [repairing, dialog, reauth])

  const dialogs = (
    <>
      <AlertDialog
        open={dialog?.kind === 'different_bank'}
        onClose={closeDialog}
        title={dialog?.kind === 'different_bank' ? dialog.title : ''}
        body={dialog?.kind === 'different_bank' ? dialog.body : ''}
      />
      <ConfirmDialog
        open={dialog?.kind === 'use_reauth'}
        mode="reconnect_use_reauth"
        title={dialog?.kind === 'use_reauth' ? dialog.title : ''}
        body={dialog?.kind === 'use_reauth' ? dialog.body : ''}
        confirmLabel="Repair"
        onCancel={closeDialog}
        onConfirm={async () => {
          if (dialog?.kind !== 'use_reauth') return
          // The live item's provider: the cached items list knows it for
          // certain (read, never fetched — nothing here needs a subscription),
          // and the disconnected bank's own provider is the same login's.
          const cached = client.getQueryData<ProviderItem[]>(keys.aggregator.items())
          const provider = cached?.find((i) => i.id === dialog.itemId)?.provider ?? dialog.provider
          setDialog(null)
          if (provider === null) {
            // No provider to open: say so (the shared no_adapter copy) rather
            // than closing the dialog and starting nothing.
            apply({ kind: 'failed', error: { kind: 'no_adapter' }, message: '' }, { bank: null, provider: null })
            return
          }
          repairTarget.current = { itemId: dialog.itemId, provider }
          setRepairing(true)
        }}
      />
    </>
  )

  return { apply, dialogs }
}

/** The provider a group's accounts were linked with, as a link provider, or
 *  `null` (a manual group, or a value this app does not know). */
export function providerOf(group: InstitutionGroup): ProviderType | null {
  if (group.item !== null) return group.item.provider
  const code = providerCode(group.accounts[0]?.provider ?? null)
  return code === 'manual' ? null : code
}

/**
 * Starts a reconnect and routes whatever it comes to through
 * `useApplyRelinkOutcome`. `start(group)` reads the relink id off the GROUP
 * (`relinkIdOf`, the disconnected account's `itemId`) and does nothing for a
 * group that cannot be reconnected.
 */
export function useReconnect(
  entryPoint: 'connections' | 'accounts_panel' | 'link_prompt',
  /** Runs when the reconnect has been started and its outcome said (a success
   *  toast, or the different-bank dialog dismissed) — for a screen with nothing
   *  left to offer, like the Link prompt. Never after a failure: that is retried. */
  afterReconnect?: () => void,
): {
  /** Rejects only on a dev-time analytics registration error, after the button is live again. */
  start: (group: InstitutionGroup) => Promise<void>
  /** The group's relink id while a reconnect is in flight, else `null`. */
  pendingId: string | null
  dialogs: ReactNode
} {
  const flow = useRelinkFlow()
  const { apply, dialogs } = useApplyRelinkOutcome()
  const [pendingId, setPendingId] = useState<string | null>(null)
  const target = useRef<{ bank: string; provider: ProviderType | null }>({ bank: '', provider: null })
  const { start: startFlow, reset, state } = flow

  const start = useCallback(
    (group: InstitutionGroup) => {
      const id = relinkIdOf(group)
      if (id === null) return Promise.resolve()
      target.current = { bank: group.name, provider: providerOf(group) }
      setPendingId(id)
      // A flow that rejects never reaches `started` or `failed`, the only states
      // that clear `pendingId` below — so clear it here, then rethrow: the dev
      // error stays visible and no false failure is reported.
      return startFlow(id, entryPoint).catch((thrown: unknown) => {
        setPendingId(null)
        throw thrown
      })
    },
    [startFlow, entryPoint],
  )

  useEffect(() => {
    if (state.status === 'started') {
      const outcome = apply(
        {
          kind: 'started',
          newItemId: state.newItemId,
          relinkStatus: state.relinkStatus,
          institutionName: state.institutionName,
        },
        { ...target.current, onDifferentBankClose: afterReconnect },
      )
      if (outcome.kind === 'toast') afterReconnect?.()
    } else if (state.status === 'failed') {
      apply({ kind: 'failed', error: state.error, message: state.message }, target.current)
    } else {
      return
    }
    // Back to idle, so the button is live again: a failure is retryable.
    setPendingId(null)
    reset()
  }, [state, apply, reset, afterReconnect])

  return { start, pendingId, dialogs }
}
